// Карточки списка: блок топ-лиг (раскрыт всегда, без каретки), страны (шапка — флаг, имя, «живые / все», каретка;
// тап раскрывает на месте), внутри — шапки лиг (герб, имя → «Скоро» лиги, звезда лиги) и строки матчей (тап → экран
// матча, звезда матча). Как MLS iOS: команды в строке отдельно не нажимаются — тап по строке открывает матч.
import { t } from '../../i18n/i18n';
import caretRight from '../../assets/icons/caret-right.svg';
import trophy from '../../assets/icons/trophy.svg';
import { FavoriteStar } from '../../ui/FavoriteStar';
import { Flag } from '../../ui/Flag';
import { MaskIcon } from '../../ui/Icon';
import { TeamLogo } from '../../ui/TeamLogo';
import { rowView } from './row';
import { countLabel, type LeagueView, type MatchView, type SectionView, type TeamView } from './sections';
import styles from './MatchCenter.module.css';

export interface SectionHandlers {
  onToggle: (section: SectionView, expanded: boolean) => void;
  onMatch: (id: number) => void;
  onLeague: (league: LeagueView) => void;
}

export function SectionCard({ section, expanded, handlers }: { section: SectionView; expanded: boolean; handlers: SectionHandlers }) {
  return (
    <section className={styles.card} aria-label={section.name}>
      <button className={`${styles.cardHeader} ${expanded ? styles.cardOpen : ''}`} disabled={section.top}
        aria-expanded={section.top ? undefined : expanded} onClick={() => handlers.onToggle(section, !expanded)}>
        <span className={styles.cardIcon}>{section.top ? <MaskIcon src={trophy} size={22} /> : <Flag src={section.flag} size={20} />}</span>
        <span className={styles.cardName}>{section.name}</span>
        <span className={styles.count}>{countLabel(section)}</span>
        {!section.top && <span className={`${styles.caret} ${expanded ? styles.caretOpen : ''}`}><MaskIcon src={caretRight} size={20} /></span>}
      </button>
      {expanded && section.leagues.map((league) => (
        <div key={league.id}>
          <div className={styles.league}>
            <button className={styles.leagueLink} onClick={() => handlers.onLeague(league)}>
              <TeamLogo src={league.logo} code={league.name} size={20} />
              <span className={styles.leagueName}>
                {league.name}{section.top && league.countryName && <span className={styles.leagueCountry}> · {league.countryName}</span>}
              </span>
            </button>
            <FavoriteStar type="league" id={league.id} compact />
          </div>
          {league.matches.map((m) => <MatchRow key={m.row.id} match={m} onOpen={handlers.onMatch} />)}
        </div>
      ))}
    </section>
  );
}

function Team({ team, home, dim }: { team: TeamView; home: boolean; dim: boolean }) {
  const logo = <span className={styles.plate}><TeamLogo src={team.logo} code={team.name} size={20} /></span>;
  return (
    <span className={`${styles.team} ${home ? styles.teamHome : ''} ${dim ? styles.dim : ''}`}>
      {!home && logo}<span className={styles.teamName}>{team.name}</span>{home && logo}
    </span>
  );
}

export function MatchRow({ match, onOpen }: { match: MatchView; onOpen: (id: number) => void }) {
  const v = rowView(match.row, t('matchCenter.tbd'));
  const open = () => onOpen(match.row.id);
  return (
    <div className={styles.match} role="link" tabIndex={0} data-match-id={match.row.id} onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter') open(); }}>
      <div className={styles.caption}>
        {v.minute && <span className={styles.minute}>{v.minute}</span>}
        <span className={styles.captionText}>{v.caption}</span>
      </div>
      <FavoriteStar type="match" id={match.row.id} compact className={styles.star} />
      <div className={styles.teams}>
        <Team team={match.home} home dim={v.dim === 'home'} />
        <span className={`${styles.pill} ${v.live ? styles.pillLive : ''} ${v.pill.kind === 'time' ? styles.pillTime : ''}`}>
          {v.pill.kind === 'time' ? v.pill.text : <>
            {v.penalty && <span className={styles.pen}>{v.penalty.home}</span>}
            <span className={styles.side}>{v.pill.home}</span>:<span className={styles.side}>{v.pill.away}</span>
            {v.penalty && <span className={styles.pen}>{v.penalty.away}</span>}
          </>}
        </span>
        <Team team={match.away} home={false} dim={v.dim === 'away'} />
      </div>
    </div>
  );
}

/** Скелетон списка — как MatchCenterSkeletonView: две полосы и три карточки по две строки. */
export function MatchCenterSkeleton() {
  return (
    <div className={styles.skeleton} role="status" aria-live="polite" aria-label={t('shell.loading')}>
      <span className={styles.sk} style={{ height: 40 }} aria-hidden="true" />
      <span className={styles.sk} style={{ height: 48 }} aria-hidden="true" />
      {[0, 1, 2].map((i) => (
        <div key={i} className={styles.skCard} aria-hidden="true">
          <span className={styles.sk} style={{ width: '60%', height: 20 }} />
          <span className={styles.sk} style={{ height: 48 }} />
          <span className={styles.sk} style={{ height: 48 }} />
        </div>
      ))}
    </div>
  );
}
